import React from 'react';
import { useTranslation } from 'react-i18next';
import { Columns2, Download, MoreVertical, RotateCcw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import InitialsAvatar from '../../common/InitialsAvatar';
import RelatedAnalysisPanel from '../RelatedAnalysisPanel';
import { DATE_TIME_FORMAT, formatDateSafe, formatFileSize } from '../../../utils/dateUtils';
import type { DocumentVersion } from '@/types/documents';

interface VersionCardProps {
  version: DocumentVersion;
  onSelect: (version: DocumentVersion | null) => void;
  onDownload: (version: DocumentVersion) => void;
  /** Omitted until a backend restore endpoint exists; the control hides when unset. */
  onRestore?: (version: DocumentVersion) => void;
  onCompare: () => void;
}

const VersionCard: React.FC<VersionCardProps> = ({
  version,
  onSelect,
  onDownload,
  onRestore,
  onCompare,
}) => {
  const { t, i18n } = useTranslation();
  const analysis = version.relatedAnalysis ?? [];

  return (
    <Card className="max-w-[400px]">
      <CardContent className="p-4">
        <div className="mb-4 flex items-start justify-between gap-2">
          <div className="min-w-0">
            {/* Not a heading: this sits inside a timeline entry, and a div keeps
                the nested badge valid markup. */}
            <div className="revy-cjk text-lg font-medium leading-[1.5]">
              {t('documents.versionWithNumber', { version: version.versionNumber })}
              {version.isCurrentVersion && (
                <Badge className="ml-2 align-middle font-medium">
                  {t('documents.currentVersion')}
                </Badge>
              )}
            </div>
            <p
              title={version.fileName}
              className="revy-cjk truncate text-sm leading-[1.5] text-muted-foreground"
            >
              {version.fileName}
            </p>
          </div>

          <DropdownMenu onOpenChange={(open) => onSelect(open ? version : null)}>
            <DropdownMenuTrigger asChild>
              <TooltipIconButton
                size="icon-sm"
                label={t('common.actions')}
                icon={<MoreVertical strokeWidth={1.8} />}
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="revy-ui w-56">
              <DropdownMenuItem
                className="revy-cjk leading-[1.5]"
                onSelect={() => onDownload(version)}
              >
                <Download strokeWidth={1.8} />
                {t('common.download')}
              </DropdownMenuItem>
              {!version.isCurrentVersion && (
                <>
                  <DropdownMenuItem className="revy-cjk leading-[1.5]" onSelect={onCompare}>
                    <Columns2 strokeWidth={1.8} />
                    {t('documents.compareWithCurrent')}
                  </DropdownMenuItem>
                  {onRestore && (
                    <DropdownMenuItem
                      className="revy-cjk leading-[1.5]"
                      onSelect={() => onRestore(version)}
                    >
                      <RotateCcw strokeWidth={1.8} />
                      {t('documents.restoreThisVersion')}
                    </DropdownMenuItem>
                  )}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="mb-4 flex items-center gap-2">
          <InitialsAvatar name={version.uploadedBy} size="sm" decorative />
          <span className="revy-cjk text-sm leading-[1.5] text-muted-foreground">
            {version.uploadedBy}
          </span>
        </div>

        <p className="revy-figure mb-1 text-sm text-muted-foreground">
          {formatDateSafe(version.uploadedAt, DATE_TIME_FORMAT, { t, language: i18n.language })}
        </p>
        <p className="revy-figure mb-4 text-sm text-muted-foreground">
          {formatFileSize(version.fileSize)}
        </p>

        {version.changesSummary && (
          <p className="revy-cjk mb-4 text-sm leading-[1.5]">{version.changesSummary}</p>
        )}

        {analysis.length > 0 && (
          <RelatedAnalysisPanel
            entries={analysis}
            heading={t('documents.deepAnalysisCompleted')}
            className="mb-4"
          />
        )}

        <div className="flex flex-wrap gap-1">
          <Button variant="ghost" size="sm" onClick={() => onDownload(version)}>
            <Download strokeWidth={1.8} />
            <span className="revy-cjk">{t('common.download')}</span>
          </Button>
          {!version.isCurrentVersion && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  onSelect(version);
                  onCompare();
                }}
              >
                <Columns2 strokeWidth={1.8} />
                <span className="revy-cjk">{t('documents.compare')}</span>
              </Button>
              {onRestore && (
                <Button variant="ghost" size="sm" onClick={() => onRestore(version)}>
                  <RotateCcw strokeWidth={1.8} />
                  <span className="revy-cjk">{t('documents.restore')}</span>
                </Button>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default VersionCard;
